
import { supabase } from "@/integrations/supabase/client";
import { fetchWithRetry } from "./fetchWithRetry";
import { toast } from "@/hooks/use-toast";

export const fetchPlanRoutines = async (planId: string) => {
  const { data: planRoutines, error: routinesError } = await fetchWithRetry(async () => {
    return supabase
      .from("workout_plan_routines")
      .select("routine_id, position")
      .eq("workout_plan_id", planId)
      .order("position", { ascending: true });
  });

  if (routinesError) throw routinesError;
  return planRoutines;
};

export const fetchRoutineDetails = async (routineId: string) => {
  try {
    const { data: routine, error: routineError } = await fetchWithRetry(async () => {
      return supabase
        .from("routines")
        .select("id, name")
        .eq("id", routineId)
        .maybeSingle();
    });

    if (routineError) throw routineError;
    if (!routine) return null;

    const { data: exercises, error: exercisesError } = await fetchWithRetry(async () => {
      return supabase
        .from("exercises")
        .select("id, name, sets, reps, weight, set_data, video_url, exercise_rest_time, set_rest_time, position")
        .eq("routine_id", routineId);
    });

    if (exercisesError) throw exercisesError;

    // Convertir los tiempos de descanso a números si existen, pero sin aplicar valores predeterminados
    const formattedExercises = exercises?.map(exercise => ({
      ...exercise,
      exercise_rest_time: exercise.exercise_rest_time !== null ? Number(exercise.exercise_rest_time) : null,
      set_rest_time: exercise.set_rest_time !== null ? Number(exercise.set_rest_time) : null,
      set_data: exercise.set_data || []
    })) || [];

    return {
      ...routine,
      exercises: formattedExercises
    };
  } catch (error) {
    console.error(`Error processing routine ${routineId}:`, error);
    toast({
      variant: "destructive",
      title: "Error",
      description: "No se pudieron cargar algunos ejercicios. Por favor, intenta de nuevo.",
    });
    return null;
  }
};

export const fetchRoutinesWithExercises = async (planRoutines: any[]) => {
  const batchSize = 3;
  const routinesWithExercises = [];

  for (let i = 0; i < planRoutines.length; i += batchSize) {
    const batch = planRoutines.slice(i, i + batchSize);
    console.log(`Processing batch ${i / batchSize + 1} of ${Math.ceil(planRoutines.length / batchSize)}`);
    
    const batchResults = await Promise.all(
      batch.map(async pr => {
        const r = await fetchRoutineDetails(pr.routine_id);
        if (r) return { ...r, plan_position: pr.position ?? 0 };
        return null;
      })
    );
    routinesWithExercises.push(...batchResults.filter(Boolean));
  }

  return routinesWithExercises.sort((a, b) => (a.plan_position ?? 0) - (b.plan_position ?? 0));
};
